import {makeStyles} from "tss-react/mui";
import { GridColDef, GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField } from "@mui/material";
import { AdminTableStringFilterInput } from "@/components/common/table/AdminTableStringFilterInput";
import { AdminTableNumericFilterInput } from "@/components/common/table/AdminTableNumericFilterInput";
import usePersistedFilterModel from "@/hooks/usePersistedFilterModel";
import { getFromLocalStorage } from "@/utils/common-utilities";
import { UserFiltersDtoOperator, UsersControllerListParams } from "../data";
import { useCallback } from "react";
import {getAppDateInputFormat} from "@/app2/shared/utils/dateManipulation";

export type DataGridColumn<T> = GridColDef<T> & {type: "date" | "dateTime" | "string" | "number" | "boolean"};

const useStyles = makeStyles()((theme) => ({
    dateInput: {
        width: "180px",
        "& .MuiInputAdornment-root": {
            "& svg path": {
                fill: theme.palette.common.white
            }
        }
    }
}));

interface useCustomDataGridProps {
    filterStorageKey?: string;
    defaultFilterSettings?: any;
}

const useCustomDataGrid = <T, >({filterStorageKey = "", defaultFilterSettings = {}}: useCustomDataGridProps) => {
    const {classes} = useStyles();
    const [persistedFilterModel, handleFilterModelChange] = usePersistedFilterModel(filterStorageKey);
    const gridFilterModel = filterStorageKey ? persistedFilterModel : undefined;

    const getPersistedFilterModel = useCallback(() => {
        if (!filterStorageKey) {
            return defaultFilterSettings;
        }
        const filterModel = getFromLocalStorage<GridFilterModel>(filterStorageKey);
        if (filterModel){
            return {
                ...defaultFilterSettings,
                filters: {
                    operator: filterModel.linkOperator,
                    conditions: filterModel.items.map(e => mapOp(e))
                }
            }
        }

        return defaultFilterSettings;
    }, [filterStorageKey, defaultFilterSettings]);

    const assignFilters = (_column: DataGridColumn<T>): GridColDef<T> => {
        const column = {..._column}
    
        if(column.filterOperators)
            return column;
    
        const dateField = (props: any): JSX.Element => {
            return (
                <DatePicker
                    inputFormat={getAppDateInputFormat()}
                    label="Value"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            className={classes.dateInput}
                            variant="standard"
                            inputProps={{
                                ...params.inputProps,
                                placeholder: getAppDateInputFormat()
                            }}
                        />
                    )}
                    value={props.item.value ? new Date(props.item.value) : null} // Ensure it's a Date object
                    onChange={(newValue: any) => {
                        if (newValue && !newValue.isValid()) {
                            return;
                        }
                        
                        props.applyValue({
                            ...props.item,
                            value: newValue ? newValue.toISOString() : null // Store as ISO string or other format needed
                        });
                    }}
                />
            );
        };
    
        // Define filter operators for specific types
        const stringFilterOperators = ([
            {label: "contains", value: "co", InputComponent: AdminTableStringFilterInput},
            {label: "equals", value: "eq", InputComponent: AdminTableStringFilterInput},
            {label: "not equal to", value: "ne", InputComponent: AdminTableStringFilterInput},
            {label: "starts with", value: "sw", InputComponent: AdminTableStringFilterInput},
            {label: "ends with", value: "ew", InputComponent: AdminTableStringFilterInput},
            {label: "exists", value: "pr"}
        ]);
    
        const numberFilterOperators = ([
            {label: "greater than", value: "gt", InputComponent: AdminTableNumericFilterInput},
            {label: "less than", value: "lt", InputComponent: AdminTableNumericFilterInput},
            {label: "greater than or equal to", value: "ge", InputComponent: AdminTableNumericFilterInput},
            {label: "less than or equal to", value: "le", InputComponent: AdminTableNumericFilterInput},
            {label: "equals", value: "eq", InputComponent: AdminTableNumericFilterInput},
            {label: "not equal to", value: "ne", InputComponent: AdminTableNumericFilterInput}
        ]);
    
        const dateFilterOperators = ([
            {label: "greater than", value: "gt", InputComponent: dateField},
            {label: "less than", value: "lt", InputComponent: dateField},
            {label: "greater than or equal to", value: "ge", InputComponent: dateField},
            {label: "less than or equal to", value: "le", InputComponent: dateField},
            {label: "equals", value: "eq", InputComponent: dateField},
            {label: "not equal to", value: "ne", InputComponent: dateField},
        ]);
    
        // Add filter operators based on inferred field type
        if (column.type === "string") {
            column.filterOperators = stringFilterOperators.map(e => ({...e, getApplyFilterFn: () => null}));
        } else if (column.type === "number") {
            column.filterOperators = numberFilterOperators.map(e => ({...e, getApplyFilterFn: ()=> null}));
        } else if (column.type === "date") {
            column.filterOperators = dateFilterOperators.map(e => ({...e, getApplyFilterFn: ()=> null}));
        }
    
        return column;
    }

    return{
        const: {
            filterModel: gridFilterModel,
        },
        api: {
            assignFilters,
            getPersistedFilterModel,
            handleFilterModelChange
        }
    };
}

const mapOp = (e: GridFilterItem) => {
    return {operator: e.operatorValue as UserFiltersDtoOperator, field: e.columnField as any, value: decodeURIComponent(e.value)};
}

export default useCustomDataGrid;